import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  getV1MeRetrieveQueryOptions,
  getV1QuizAdminPushQuizzesListQueryKey,
  useV1QuizAdminCapabilities,
  useV1QuizAdminPushQuizzesCreate,
} from '@/api/client'
import type { AdminPushQuizWriteRequest } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { QuizAuthoringGate } from '@/components/quiz/quiz-capability-notice'
import { quizCapabilitiesOrClosed } from '@/components/quiz/quiz-capabilities'
import { PushQuizForm } from '@/components/push-quiz/push-quiz-form'

export const Route = createFileRoute('/_panel/zadania/nowe')({
  beforeLoad: async ({ context }) => {
    // Operator-only area (admin + superadmin): everyone else is bounced out
    // before any quiz data loads (FR-001/FR-002).
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'admin' && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: PushQuizComposePage,
})

/**
 * US2 compose route (T014): the full-page editor in `create` mode. Submit
 * creates a DRAFT and navigates to its detail; a 400 lands back on the form's
 * fields (incl. offender-uid row marking) and creates nothing
 * (FR-006–FR-009, FR-017, FR-019).
 */
function PushQuizComposePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const capabilitiesQuery = useV1QuizAdminCapabilities()
  const capabilities = quizCapabilitiesOrClosed(
    capabilitiesQuery.data?.status === 200 ? capabilitiesQuery.data.data : undefined,
  )
  const createMutation = useV1QuizAdminPushQuizzesCreate()

  const handleSubmit = async (payload: AdminPushQuizWriteRequest) => {
    const response = await createMutation.mutateAsync({ data: payload })
    if (response.status === 201) {
      await queryClient.invalidateQueries({ queryKey: getV1QuizAdminPushQuizzesListQueryKey() })
      await navigate({ to: '/zadania/$uid', params: { uid: response.data.uid } })
    }
    return response
  }

  return (
    <>
      <PageHeader title="Nowy quiz" />
      <div className="p-6">
        <QuizAuthoringGate capabilities={capabilities}>
          <PushQuizForm
            mode="create"
            submitLabel="Utwórz szkic"
            onSubmit={handleSubmit}
            onCancel={() => void navigate({ to: '/zadania' })}
          />
        </QuizAuthoringGate>
      </div>
    </>
  )
}
